(function () {
  'use strict';

  /* Owns the animation for same-page anchor links.
     `html { scroll-behavior: smooth }` makes the browser animate a hash jump,
     but any third-party `scrollIntoView` issued while that animation is in
     flight replaces it, and the page stops wherever it had got to. The Tolstoy
     widget does exactly that: it re-centres the active card in its carousel
     with `scrollIntoView({ behavior: 'smooth', block: 'nearest' })` as it
     enters the viewport, so a CTA that has to travel up past it parks on the
     widget instead of reaching its target.
     Driving the scroll from rAF with instant writes means an interloper only
     owns the position until the next frame, and the destination is recomputed
     every frame so a late layout shift cannot leave us short either. */

  var MIN_DURATION = 320;
  var MAX_DURATION = 900;
  var PX_PER_MS = 2.5;

  var animation = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Writes have to land in the frame they are made. `behavior: 'instant'`
     would throw on browsers that predate the enum value, so the CSS is
     overridden inline for the length of the animation instead. */
  function holdScrollBehavior() {
    var root = document.documentElement;
    var previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    return function () {
      root.style.scrollBehavior = previous;
    };
  }

  function maxScroll() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  /* Both insets the browser would have applied itself: the target's own
     scroll-margin (`.section[id]` in theme.css) and the scroller's
     scroll-padding (`imws.css` sets it to the sticky header height). Missing
     either one lands us short of the native position, and the hash assigned
     at the end would then snap the page the rest of the way. */
  function destinationFor(target) {
    var scroller = document.scrollingElement || document.documentElement;
    var margin = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    var padding = parseFloat(window.getComputedStyle(scroller).scrollPaddingTop) || 0;
    var top = target.getBoundingClientRect().top + window.scrollY - margin - padding;
    return Math.max(0, Math.min(top, maxScroll()));
  }

  function targetFromHash(hash) {
    var id = hash.slice(1);
    if (!id) return null;
    var target = document.getElementById(id);
    if (!target) {
      try {
        target = document.getElementById(decodeURIComponent(id));
      } catch (e) {
        /* malformed escape sequence; treat as no match */
      }
    }
    return target && target.getClientRects().length ? target : null;
  }

  /* A target inside a drawer or modal needs that panel scrolled, not the page.
     The browser already does this correctly, so leave those links alone. */
  function hasScrollableAncestor(target) {
    for (var el = target.parentElement; el && el !== document.body; el = el.parentElement) {
      var overflow = window.getComputedStyle(el).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight + 1) return true;
    }
    return false;
  }

  function stop() {
    if (!animation) return;
    cancelAnimationFrame(animation.frame);
    animation.release();
    animation = null;
  }

  function animateTo(target, done) {
    stop();

    var from = window.scrollY;
    var distance = Math.abs(destinationFor(target) - from);

    animation = {
      target: target,
      from: from,
      start: performance.now(),
      duration: Math.min(MAX_DURATION, Math.max(MIN_DURATION, distance / PX_PER_MS)),
      release: holdScrollBehavior(),
      frame: 0
    };

    var step = function (now) {
      if (!animation) return;
      var progress = Math.min(1, (now - animation.start) / animation.duration);
      var eased = 1 - Math.pow(1 - progress, 3);
      var to = destinationFor(animation.target);
      window.scrollTo(0, animation.from + (to - animation.from) * eased);
      if (progress < 1) {
        animation.frame = requestAnimationFrame(step);
        return;
      }
      animation.release();
      animation = null;
      done();
    };
    animation.frame = requestAnimationFrame(step);
  }

  function isPlainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  /* Bubble phase, and only when nothing else claimed the click: a component
     with its own handler for the link (scroll-spy, the `:target` login
     toggle's siblings, any app) calls preventDefault first and keeps control. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || !isPlainClick(event)) return;
    if (!(event.target instanceof Element)) return;

    var link = event.target.closest('a[href*="#"]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    if (link.dataset.anchorScroll === 'off') return;

    /* Same-document only: a hash on another page is a real navigation. */
    var url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    if (url.search !== window.location.search) return;

    var target = targetFromHash(url.hash);
    if (!target || hasScrollableAncestor(target)) return;

    event.preventDefault();

    /* The hash is assigned natively rather than pushed with pushState, which
       leaves `:target` stale — `assets/customer.css` drives the login/recover
       toggle off it. By this point the page is already parked where the hash
       points, so the fragment navigation it triggers moves nothing. */
    var settle = function () {
      if (url.hash !== window.location.hash) window.location.hash = url.hash;

      /* Keyboard and screen-reader users continue from the target, not from
         the link they just left. preventScroll because we own the position. */
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };

    if (prefersReducedMotion()) {
      var release = holdScrollBehavior();
      window.scrollTo(0, destinationFor(target));
      release();
      settle();
      return;
    }

    animateTo(target, settle);
  });

  /* Any deliberate scroll by the visitor outranks an animation in progress.
     The URL is left alone in that case, so nothing pulls them back. */
  ['wheel', 'touchstart', 'keydown'].forEach(function (name) {
    window.addEventListener(name, stop, { passive: true });
  });
})();
